/**
 * App shell shared in shape by all three frontends.
 *
 * Resolves the three things every screen assumes: are you signed in, who are
 * you, and what does your plan include. Nothing below this renders until all
 * three are known — a screen that guesses at entitlements and then corrects
 * itself is worse than one that waits 200ms.
 */

import { useEffect, type ReactNode } from 'react';
import {
  ApiError,
  EntitlementsProvider,
  useCompleteMfa,
  useEntitlementsQuery,
  useLogin,
  useMe,
  useSession,
} from '@restaurant/api-client';
import { ErrorNotice, Loading, SignInForm } from '@restaurant/ui';

export function Shell({
  appName,
  tagline,
  children,
}: {
  appName: string;
  tagline: string;
  children: ReactNode;
}) {
  const session = useSession();
  const login = useLogin();
  const completeMfa = useCompleteMfa();
  // `reset` keeps the same identity for the life of the mutation; the object
  // around it does not. Pulling the two out by name is what stops the effect
  // below from re-running on every render.
  const { reset: forgetSignIn } = login;
  const { reset: forgetCode } = completeMfa;

  // The shell outlives the session, and so do these two mutations. Without
  // this, a sign-in that went through a second factor left its challenge in
  // `login.data` — and the next sign-out on the same tablet opened straight
  // on the code step, asking for a code against a challenge the server threw
  // away minutes ago.
  useEffect(() => {
    if (!session.isAuthenticated) return;
    forgetSignIn();
    forgetCode();
  }, [session.isAuthenticated, forgetSignIn, forgetCode]);

  if (!session.isAuthenticated) {
    return (
      <SignInForm
        appName={appName}
        tagline={tagline}
        submitting={login.isPending}
        error={login.error ? signInMessage(login.error) : null}
        onSubmit={(credentials) => login.mutate(credentials)}
        // The form reads the challenge out of this itself and asks for a code;
        // all the app has to know is what to do with one once it is typed.
        result={login.data}
        onSubmitCode={(challengeToken, code) => completeMfa.mutate({ challengeToken, code })}
        codeSubmitting={completeMfa.isPending}
        codeError={completeMfa.error}
        onRestart={() => {
          forgetSignIn();
          forgetCode();
        }}
      />
    );
  }

  return <AuthenticatedShell>{children}</AuthenticatedShell>;
}

function AuthenticatedShell({ children }: { children: ReactNode }) {
  const me = useMe();
  const entitlements = useEntitlementsQuery();

  if (me.isPending || entitlements.isPending) {
    return <Loading label="Signing you in" />;
  }

  if (me.isError || entitlements.isError) {
    const error = me.error ?? entitlements.error;
    return (
      <div style={{ padding: 'var(--space-5)' }}>
        <ErrorNotice
          title="Could not load your account"
          message={error instanceof Error ? error.message : 'Unknown error'}
        />
      </div>
    );
  }

  return <EntitlementsProvider data={entitlements.data}>{children}</EntitlementsProvider>;
}

/**
 * Sign-in failures, phrased for the person at the terminal.
 *
 * A 401 here is almost always a typo, so it says so. A network failure is not
 * the user's fault and must not read like a rejected password — during service
 * that difference decides whether someone retries or goes looking for a manager.
 */
function signInMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Incorrect restaurant, email, or password.';
    if (error.code === 'network_unreachable') {
      return 'Cannot reach the server. Check the connection and try again.';
    }
    return error.message;
  }
  return 'Sign-in failed. Try again.';
}
